import { Grid } from '@mantine/core';
import { HubConnectionState } from '@microsoft/signalr';
import clsx from 'clsx';
import {
  ComponentProps,
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import { Modal as RSModal } from 'reactstrap';
import { BulkEditStatus } from 'src/components/common/BulkActions/BulkEditStatus';
import {
  createSquareMarketplaceLogos,
  LogoDimensions,
} from 'src/components/common/MarketplaceLogo';
import { useActivePosEntityContext } from 'src/contexts/ActivePosEntityContext';
import { useAppContext } from 'src/contexts/AppContext';
import { useBulkEditHubContext } from 'src/contexts/BulkEditHubContext';
import { useCatalogDataContext } from 'src/contexts/CatalogDataContext';
import { Content } from 'src/contexts/ContentContext';
import { useMultiSelectionContext } from 'src/contexts/MultiSelectionContext';
import { vars } from 'src/core/themes';
import { Stack, Switch } from 'src/core/ui';
import { BulkEditFooter } from 'src/dialogs/BulkEdits/common/BulkEditFooter';
import { useBulkEditHub } from 'src/hooks/useBulkEditHub';
import { useComputedState } from 'src/hooks/useComputedState';
import { useGetEventFullInfo } from 'src/hooks/useGetEventFullInfo';
import { ContentId } from 'src/utils/constants/contentId';
import { MARKET_PLACE_TO_CID } from 'src/utils/constants/contentIdMaps';
import { getIsInternationalEvent } from 'src/utils/eventWithDataUtils';
import { isMarketplaceActive } from 'src/utils/userUtils';
import {
  ActionOutboxEntityType,
  BulkActionType,
  BulkEditPreview,
  BulkEditPreviewWithDetails,
  BulkEditProgress,
  BulkEditStep,
  Event,
  ListingDetails,
  Marketplace,
  MarketplaceAccountSetting,
} from 'src/WebApiController';

import * as styles from './BulkEditBroadcastSettingsMobileContent.css';

export type MarketplaceBroadcastSelectionMode = 'all' | 'none' | 'default';

export type BulkEditBroadcastSettingsMobileContentProps = ComponentProps<
  typeof RSModal
> & {
  updateKey: string;
  onOkay: (
    marketplacesToBroadcast: Marketplace[] | null,
    marketplacesToUnbroadcast: Marketplace[] | null,
    supportBackgroundProcess?: boolean,
    onPreviewReceived?: (preview: BulkEditPreviewWithDetails) => void,
    preview?: BulkEditPreview
  ) => void;
  onCancel: () => void;
  event?: Event;
};

export function BulkEditBroadcastSettingsMobileContent({
  updateKey,
  onOkay,
  onCancel,
  event,
}: BulkEditBroadcastSettingsMobileContentProps) {
  const { setActivePosEntity } = useActivePosEntityContext<ListingDetails>();
  const {
    eventsExpansion: { refreshExpandedListItems },
  } = useCatalogDataContext();
  const { setSelectionMode } = useMultiSelectionContext();
  const { setProgress, setPreview, setMainDialogOpened } =
    useBulkEditHubContext();
  const [isLoading, setIsLoading] = useState(false);

  const onClose = useCallback(
    async (
      newProgress?: BulkEditProgress,
      closeMainDialogForPopover?: boolean
    ) => {
      if (newProgress?.step === BulkEditStep.Done) {
        setIsRefreshing(true);
        setActivePosEntity(0);

        await refreshExpandedListItems();
        setSelectionMode(undefined);
        if (closeMainDialogForPopover) {
          setMainDialogOpened(false);
        } else {
          setProgress(undefined);
          setPreview(undefined);
        }
        setIsRefreshing(false);
        // progress kept to allow the BulkEditStatusPopover to show the result
      }
      onCancel();
    },
    [
      onCancel,
      refreshExpandedListItems,
      setActivePosEntity,
      setMainDialogOpened,
      setPreview,
      setProgress,
      setSelectionMode,
    ]
  );

  const onBulkEditDone = useCallback(
    async (doneProgress: BulkEditProgress, finalErrors: string[]) => {
      if (finalErrors.length === 0 && doneProgress.step === BulkEditStep.Done) {
        onClose(doneProgress, true);
      }
    },
    [onClose]
  );

  const { bulkEditHub, progress, preview, initJob } = useBulkEditHub(
    ActionOutboxEntityType.Listing,
    BulkActionType.UpdateBroadcastSettings,
    updateKey,
    onBulkEditDone
  );

  useEffect(() => {
    initJob();
    return () => {
      setMainDialogOpened(false);
      if (progress) {
        setSelectionMode(undefined);
      }
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const hasBackgroundBulkEditFeature =
    bulkEditHub?.state === HubConnectionState.Connected;

  const { venue } = useGetEventFullInfo(event);
  const isInternationalEvent = getIsInternationalEvent(venue?.ctryCode);
  const { loginContext } = useAppContext();

  const sanitizedMarketplaces = useMemo(() => {
    const marketplaceSettings =
      loginContext?.user?.activeAccount?.marketplaceSettings ?? [];
    return marketplaceSettings.filter(
      (mk) =>
        event == null || !isInternationalEvent || mk.mkp === Marketplace.StubHub
    );
  }, [
    event,
    isInternationalEvent,
    loginContext?.user?.activeAccount?.marketplaceSettings,
  ]);
  const defaultActiveMarketplaces = useMemo(() => {
    return sanitizedMarketplaces
      .filter((mk) => isMarketplaceActive(mk))
      .map((mp) => mp.mkp);
  }, [sanitizedMarketplaces]);

  const [marketplacesToBroadcast, setMarketplacesToBroadcast] =
    useComputedState<Marketplace[]>(defaultActiveMarketplaces);
  const marketplacesToUnbroadcast = useMemo(() => {
    return sanitizedMarketplaces
      .map((mp) => mp.mkp)
      .filter((mkp) => !marketplacesToBroadcast.includes(mkp));
  }, [marketplacesToBroadcast, sanitizedMarketplaces]);

  const [isRefreshing, setIsRefreshing] = useState(false);

  const onSubmitHandler = useCallback(
    (onPreviewReceived?: (preview: BulkEditPreviewWithDetails) => void) => {
      setIsLoading(true);
      if (onPreviewReceived) {
        onOkay(null, null, false, onPreviewReceived);
      } else {
        onOkay(
          marketplacesToBroadcast,
          marketplacesToUnbroadcast,
          hasBackgroundBulkEditFeature,
          undefined,
          preview!.preview
        );
      }
      setIsLoading(false);
    },
    [
      onOkay,
      marketplacesToBroadcast,
      marketplacesToUnbroadcast,
      preview,
      hasBackgroundBulkEditFeature,
    ]
  );
  const submittButtonRef = useRef<HTMLButtonElement>(null);

  return (
    <Stack direction="column" width="full" height="full">
      <div style={{ flex: 1, minHeight: 0 }}>
        <BulkEditStatus
          entityType={ActionOutboxEntityType.Listing}
          isLoading={isRefreshing || isLoading}
          updateKey={updateKey}
        >
          <MarketplaceBroadcastForm
            marketplaces={sanitizedMarketplaces}
            marketplacesToBroadcast={marketplacesToBroadcast}
            setMarketplacesToBroadcast={setMarketplacesToBroadcast}
          />
        </BulkEditStatus>
      </div>

      <Stack justifyContent="spaceBetween" width="full">
        <BulkEditFooter
          entityType={ActionOutboxEntityType.Listing}
          isLoading={isLoading}
          hasBackgroundBulkEditFeature={hasBackgroundBulkEditFeature}
          onClose={onClose}
          onSubmit={onSubmitHandler}
          submittButtonRef={submittButtonRef}
        />
      </Stack>
    </Stack>
  );
}

const MarketplaceBroadcastForm = ({
  marketplaces,
  marketplacesToBroadcast,
  setMarketplacesToBroadcast,
}: {
  marketplaces: MarketplaceAccountSetting[];
  marketplacesToBroadcast: Marketplace[];
  setMarketplacesToBroadcast: (mkps: Marketplace[]) => void;
}) => {
  const areAllSelected = useMemo(() => {
    return marketplaces.every((mp) => marketplacesToBroadcast.includes(mp.mkp));
  }, [marketplaces, marketplacesToBroadcast]);

  const onToggleAllMarketplaces = useCallback(
    (isChecked: boolean) => {
      if (isChecked) {
        setMarketplacesToBroadcast(marketplaces.map((mp) => mp.mkp));
      } else {
        setMarketplacesToBroadcast([]);
      }
    },
    [marketplaces, setMarketplacesToBroadcast]
  );

  const onToggleMarketplaceBroadcast = useCallback(
    (marketplace: Marketplace) => (broadcast: boolean) => {
      const toBroadcast = new Set(marketplacesToBroadcast);
      if (broadcast) {
        toBroadcast.add(marketplace);
      } else {
        toBroadcast.delete(marketplace);
      }
      setMarketplacesToBroadcast(Array.from(toBroadcast));
    },
    [marketplacesToBroadcast, setMarketplacesToBroadcast]
  );

  return (
    <Stack
      direction="column"
      gap="xxl"
      style={{ padding: vars.spacing.xs, color: vars.color.textPrimaryV2 }}
    >
      <Stack width="full" direction="column" gap="l">
        <Stack justifyContent="spaceBetween" alignItems="center">
          <span>
            <Content id={ContentId.ListOnAllPlatforms} />
          </span>
          <Switch
            checked={areAllSelected}
            onCheckedChange={onToggleAllMarketplaces}
          />
        </Stack>
        <Stack width="full" direction="column" gap="l">
          <MarketplaceBroadcastHeader />
          {marketplaces.map((mp) => {
            return (
              <MarketplaceBroadcastTile
                key={mp.mkp}
                marketplace={mp.mkp}
                isToBroadcast={marketplacesToBroadcast.includes(mp.mkp)}
                onToggleBroadcast={onToggleMarketplaceBroadcast(mp.mkp)}
              />
            );
          })}
        </Stack>
      </Stack>
    </Stack>
  );
};

const MarketplaceBroadcastHeader = () => {
  return (
    <Grid align="center" style={{ fontSize: vars.typography.fontSize.sm }}>
      <Grid.Col span={5}>
        <Content id={ContentId.Marketplace} />
      </Grid.Col>
      <Grid.Col span={4}>
        <Content id={ContentId.State} />
      </Grid.Col>
      <Grid.Col span={3}>
        <Content id={ContentId.Broadcast} />
      </Grid.Col>
    </Grid>
  );
};

const MarketplaceBroadcastTile = ({
  marketplace,
  isToBroadcast,
  onToggleBroadcast,
}: {
  marketplace: Marketplace;
  isToBroadcast: boolean;
  onToggleBroadcast: (broadcast: boolean) => void;
}) => {
  const marketplaceIconProps = useMemo(() => {
    const logos = createSquareMarketplaceLogos(false);
    return {
      Icon: logos[marketplace],
      title: MARKET_PLACE_TO_CID[marketplace],
    };
  }, [marketplace]);

  return (
    <Grid align="center" className={styles.priceTileContainer}>
      <Grid.Col span={5}>
        <MarketplaceIcon {...marketplaceIconProps} />
      </Grid.Col>
      <Grid.Col span={4}>
        <span
          className={clsx(styles.statusLabel, {
            [styles.activeLabel]: isToBroadcast,
          })}
        >
          <Content id={isToBroadcast ? ContentId.Active : ContentId.Inactive} />
        </span>
      </Grid.Col>
      <Grid.Col span={3}>
        <Switch checked={isToBroadcast} onCheckedChange={onToggleBroadcast} />
      </Grid.Col>
    </Grid>
  );
};

const MarketplaceIcon = ({
  Icon,
  title,
}: {
  Icon: React.FC<LogoDimensions>;
  title: ContentId | string;
}) => {
  return (
    <Stack gap="s" alignItems="center" width="fit" className={styles.baseLabel}>
      <Icon width={24} height={24} />
      <span>
        <Content id={title} />
      </span>
    </Stack>
  );
};
